import { motion } from "framer-motion";
import { Award, GraduationCap, Trophy, Star } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function AwardsSection() {
  return (
    <section
      id="awards"
      className="py-20 md:py-32 bg-white"
      data-testid="awards-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-sm font-medium tracking-widest uppercase text-[#E07A5F] mb-4 block">
            Recognition
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#1A1C18] mb-6 tracking-tight"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            Training & Achievements
          </h2>
          <p className="text-[#4A4F45] text-lg leading-relaxed">
            Our commitment to excellence is recognized through official certifications and awards.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Training Certificate */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="bg-[#F9F8F4] p-8 md:p-12 rounded-3xl border border-[#E5E2D9]"
          >
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-[#2F5233] rounded-2xl flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3
                  className="text-2xl font-semibold text-[#1A1C18] mb-3"
                  style={{ fontFamily: "'Fraunces', serif" }}
                >
                  CPCRI Training Certificate
                </h3>
                <p className="text-[#4A4F45] leading-relaxed mb-4">
                  Our nursery management team has successfully completed official
                  training from the{" "}
                  <strong>
                    Central Plantation Crops Research Institute (CPCRI)
                  </strong>
                  , ensuring professional expertise in coconut seedling
                  cultivation.
                </p>
                <div className="flex items-center gap-2 text-[#2F5233]">
                  <Award className="w-5 h-5" />
                  <span className="font-medium">Training Certificate Available</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Award */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="bg-gradient-to-br from-[#E07A5F] to-[#C66A50] p-8 md:p-12 rounded-3xl relative overflow-hidden"
          >
            {/* Decorative Stars */}
            <div className="absolute top-4 right-4 opacity-20">
              <Star className="w-24 h-24 text-white fill-white" />
            </div>
            <div className="absolute bottom-4 left-4 opacity-10">
              <Star className="w-16 h-16 text-white fill-white" />
            </div>

            <div className="relative z-10">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center mb-6 award-badge">
                <Trophy className="w-10 h-10 text-white" />
              </div>
              <h3
                className="text-3xl md:text-4xl font-bold text-white mb-4"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                ASM Udhyan Ratna Award
              </h3>
              <p className="text-xl text-white/90 mb-2">2024 Recipient</p>
              <p className="text-white/80 leading-relaxed">
                Awarded in recognition of our significant contribution and
                excellence in horticulture and nursery management practices.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Achievement Image */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.3 }}
          className="mt-8 img-zoom rounded-3xl overflow-hidden"
        >
          <img
            src="https://images.unsplash.com/photo-1752159684779-0639174cdfac?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Business partnership and achievement"
            className="w-full h-[300px] md:h-[400px] object-cover"
          />
        </motion.div>
      </div>
    </section>
  );
}
